/* The dashboard's screens.
 *
 * Every number on every one of them is a row the ledger can produce, and every
 * screen has the empty state it will show for months: no shop has customers
 * yet. Nothing here fabricates a figure to fill a panel.
 *
 * What the artboard draws and this does not: a storefront editor (no customer
 * can see a shop page), reviews, campaigns, a plan ladder that contradicts the
 * ruled pricing, invoices, API keys, scheduled reports, and a spend column
 * (no amount is captured at a counter). The reading is in
 * reports/surfaces/merchant-dashboard.md.
 */
import { Fragment, useState } from "react";

import { type CopyKey, t } from "../content";

export type Overview = {
  days: number;
  stamps: number;
  rewards: number;
  members: number;
  joined: number;
  tiers: { name: string; at: number; members: number }[];
  activity: {
    type: string;
    at: string;
    who: string | null;
    detail: string | null;
    delta: number;
    branch: string | null;
    staff: string | null;
  }[];
};
export type Customer = {
  membership_id: string;
  name: string | null;
  masked: string | null;
  balance: number;
  threshold: number;
  visits: number;
  rewards: number;
  joined: string | null;
  last_earn_at: string | null;
  branch: string | null;
};
export type Branch = { id: string; name: string; address: string | null; primary: boolean; stamps: number; members: number; staff: number };
export type Staff = {
  user_id: string;
  /** The per-shop identity (036), and what the team actions take. */
  staff_id: string;
  display_name: string | null;
  role: string;
  has_pin: boolean;
  pin_locked: boolean;
  accepted: boolean;
  /** 049: the sign-in, and whether its password has been set from the link. */
  username: string | null;
  password_set: boolean;
};

/** The owner's three team actions. Each answers with the sentence to show. */
export type TeamActions = {
  add: (username: string, email: string) => Promise<{ ok: boolean; message: string }>;
  resend: (staffId: string) => Promise<{ ok: boolean; message: string }>;
  remove: (staffId: string) => Promise<{ ok: boolean; message: string }>;
};
export type Till = { device_id: string; label: string | null; state: string; last_seen_at: string | null };
export type Reward = { entry_id: string; type: string; at: string; who: string | null; reward: string | null; expires_at: string | null; branch: string | null; staff: string | null };
export type Program = {
  id: string;
  version: number;
  type: string;
  points_per_stamp: number | null;
  min_gap_minutes: number | null;
  tiers: { order: number; name: string; threshold: number; reward: string; validity_days: number | null; lasts_days: number }[];
} | null;

const dt = (iso: string | null, withTime = false) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
      })
    : t("customers.never");

export function Empty({ title, body }: { title: CopyKey; body: CopyKey }) {
  return (
    <div className="empty">
      <p className="empty-title">{t(title)}</p>
      <p className="empty-body">{t(body)}</p>
    </div>
  );
}

function Stat({ v, k }: { v: number; k: CopyKey }) {
  return (
    <div className="stat">
      <p className="stat-v">{v.toLocaleString("en-GB")}</p>
      <p className="stat-k">{t(k)}</p>
    </div>
  );
}

const KIND: Record<string, { icon: string; kind?: string }> = {
  EARN: { icon: "✓" },
  REWARD_ISSUE: { icon: "★", kind: "reward" },
  REWARD_USE: { icon: "★", kind: "reward" },
  EARN_REVERSAL: { icon: "↺", kind: "back" },
  SYNC_REJECTED: { icon: "↺", kind: "back" },
  IMPORT_OPENING: { icon: "+" },
};

/* BEFORE YOUR FIRST STAMP, and deliberately not a publish gate.
 *
 * The intake artboard draws five automatic checks in front of appearing in
 * Discover. There is no Discover listing, so there is nothing to publish to;
 * and one of its five, a contact number a customer can call, is not answerable
 * at all, because `businesses` has no such column (checked 2026-09-18, and the
 * surface reading was corrected where it said otherwise).
 *
 * What is left is the four things that must be true before this shop can take a
 * stamp, each read off a route the dashboard already loads. It is a list and
 * not a gate: nothing is withheld until it passes. */
function Ready({
  program,
  branches,
  staff,
  tills,
}: {
  program: Program;
  branches: Branch[];
  staff: Staff[];
  tills: Till[];
}) {
  const tier = program?.tiers?.[0] ?? null;
  const withPin = staff.filter((s) => s.has_pin).length;
  const seen = tills.filter((t) => t.state !== "never_seen").length;

  const checks: { key: CopyKey; done: boolean; say: string }[] = [
    {
      key: "ready.programme",
      done: Boolean(tier),
      say: tier
        ? t("ready.programme.yes", { reward: tier.reward, n: tier.threshold })
        : t("ready.programme.no"),
    },
    {
      key: "ready.branch",
      done: branches.length > 0,
      say: branches.length > 0 ? t("ready.branch.yes", { n: branches.length }) : t("ready.branch.no"),
    },
    {
      key: "ready.staff",
      done: withPin > 0,
      say: withPin > 0 ? t("ready.staff.yes", { n: withPin }) : t("ready.staff.no"),
    },
    {
      key: "ready.till",
      done: seen > 0,
      say: seen > 0 ? t("ready.till.yes", { n: seen }) : t("ready.till.no"),
    },
  ];
  const allSet = checks.every((c) => c.done);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">{t("ready.title")}</h2>
      </div>
      <p className="note">{allSet ? t("ready.allSet") : t("ready.body")}</p>
      <ul className="rows">
        {checks.map((c) => (
          <li className="row" key={c.key}>
            <span className="dot" data-kind={c.done ? "reward" : undefined} aria-hidden="true">
              {c.done ? "\u2713" : "\u00b7"}
            </span>
            <div>
              <p className="row-title">{t(c.key)}</p>
              <p className="row-meta">{c.say}</p>
            </div>
            <span className="chip" data-tone={c.done ? "good" : "warn"}>
              {c.done ? t("ready.done") : t("ready.todo")}
            </span>
          </li>
        ))}
      </ul>
      <p className="fine">{t("ready.notDiscover")}</p>
    </section>
  );
}

export function OverviewScreen({
  data,
  program,
  branches,
  staff,
  tills,
  onCustomers,
}: {
  data: Overview;
  program: Program;
  branches: Branch[];
  staff: Staff[];
  tills: Till[];
  onCustomers: () => void;
}) {
  const nothing = data.members === 0 && data.activity.length === 0;
  return (
    <>
      <div className="stats">
        <Stat v={data.stamps} k="overview.stat.stamps" />
        <Stat v={data.rewards} k="overview.stat.rewards" />
        <Stat v={data.members} k="overview.stat.members" />
        <Stat v={data.joined} k="overview.stat.joined" />
      </div>

      {nothing ? (
        <>
          <Empty title="overview.empty.title" body="overview.empty.body" />
          <Ready program={program} branches={branches} staff={staff} tills={tills} />
        </>
      ) : (
        <div className="cols">
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">{t("overview.tiers")}</h2>
            </div>
            {data.tiers.length === 0 ? (
              <p className="note">{t("overview.tiers.none")}</p>
            ) : (
              <ul className="rows">
                {data.tiers.map((tier) => (
                  <li className="row" key={tier.name}>
                    <span className="dot" aria-hidden="true">
                      {tier.at}
                    </span>
                    <div>
                      <p className="row-title">{tier.name}</p>
                      <p className="row-meta">{t("overview.tiers.at", { n: tier.at })}</p>
                    </div>
                    <span className="row-value">{tier.members}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">{t("overview.activity")}</h2>
              <button type="button" className="btn btn-quiet" onClick={onCustomers}>
                {t("overview.activity.allCustomers")}
              </button>
            </div>
            {data.activity.length === 0 ? (
              <p className="note">{t("overview.activity.none")}</p>
            ) : (
              <ul className="rows">
                {data.activity.map((a, i) => {
                  const kind = KIND[a.type] ?? { icon: "·" };
                  return (
                    <li className="row" key={`${a.at}-${i}`}>
                      <span className="dot" data-kind={kind.kind} aria-hidden="true">
                        {kind.icon}
                      </span>
                      <div>
                        <p className="row-title">
                          {a.who ?? t("customers.erased")}
                          {a.detail ? ` · ${a.detail}` : ""}
                        </p>
                        <p className="row-meta">
                          {[a.branch, a.staff, dt(a.at, true)].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <span className="row-value">{a.type.replace(/_/g, " ").toLowerCase()}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}
    </>
  );
}

export function CustomersScreen({
  customers,
  query,
  onQuery,
}: {
  customers: Customer[];
  query: string;
  onQuery: (q: string) => void;
}) {
  if (customers.length === 0 && !query) return <Empty title="customers.none.title" body="customers.none.body" />;
  const q = query.trim().toLowerCase();
  const shown = q ? customers.filter((c) => (c.name ?? "").toLowerCase().includes(q)) : customers;

  return (
    <>
      <div className="head">
        <p className="label">{t("customers.count", { n: customers.length })}</p>
        <input
          className="input search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t("customers.search")}
          aria-label={t("customers.search")}
        />
      </div>
      {shown.length === 0 ? (
        <p className="note">{t("customers.noMatch")}</p>
      ) : (
        <div className="panel">
          <div className="table-wrap">
            <table className="table-stack" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{t("customers.col.name")}</th>
                  <th role="columnheader" className="num">{t("customers.col.card")}</th>
                  <th role="columnheader" className="num">{t("customers.col.visits")}</th>
                  <th role="columnheader" className="num">{t("customers.col.rewards")}</th>
                  <th role="columnheader">{t("customers.col.last")}</th>
                  <th role="columnheader">{t("customers.col.branch")}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {shown.map((c) => (
                  <tr role="row" key={c.membership_id}>
                    <td role="cell" data-lead="">
                      <span className="cell-name">{c.name ?? t("customers.erased")}</span>
                      <span className="cell-sub">{c.masked}</span>
                    </td>
                    <td role="cell" className="num" data-label={t("customers.col.card")}>
                      {c.balance} / {c.threshold}
                    </td>
                    <td role="cell" className="num" data-label={t("customers.col.visits")}>{c.visits}</td>
                    <td role="cell" className="num" data-label={t("customers.col.rewards")}>{c.rewards}</td>
                    <td role="cell" data-label={t("customers.col.last")}>{dt(c.last_earn_at)}</td>
                    <td role="cell" data-label={t("customers.col.branch")}>{c.branch ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fine">{t("customers.masked")}</p>
        </div>
      )}
    </>
  );
}

/* THE GAP A FIRST CARD IS STARTED WITH, said on the screen and sent with the card
 * (2026-09-18). It used to be the server's default, never shown until the card
 * existed, so the end-to-end test's owner learned the 90-minute rule afterwards.
 * One number, stated and sent from here, so the two cannot differ. */
export const FIRST_CARD_GAP_MINUTES = 90;

function StartCard({ onStart }: { onStart: (stamps: number, reward: string) => Promise<string | null> }) {
  const [stamps, setStamps] = useState(8);
  const [reward, setReward] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <section className="panel form-panel">
      <p className="empty-title">{t("programme.none.title")}</p>
      <p className="lead">{t("programme.none.body")}</p>
      <form
        className="form"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          setProblem(await onStart(stamps, reward.trim()));
          setBusy(false);
        }}
      >
        <label className="field">
          <span className="field-label">{t("programme.create.stamps")}</span>
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={2}
            max={50}
            value={stamps}
            onChange={(e) => setStamps(Number(e.target.value))}
          />
        </label>
        <label className="field">
          <span className="field-label">{t("programme.create.reward")}</span>
          <input
            className="input"
            maxLength={80}
            placeholder={t("programme.create.rewardHint")}
            value={reward}
            onChange={(e) => setReward(e.target.value)}
          />
        </label>
        <p className="fine">{t("programme.create.gap", { n: FIRST_CARD_GAP_MINUTES })}</p>
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
        <button type="submit" className="btn btn-primary" aria-busy={busy}>
          {t("programme.create.submit")}
        </button>
      </form>
      <p className="fine">{t("programme.create.note")}</p>
    </section>
  );
}

export function ProgrammeScreen({
  program,
  onStart,
}: {
  program: Program;
  onStart: (stamps: number, reward: string) => Promise<string | null>;
}) {
  if (!program) return <StartCard onStart={onStart} />;
  const first = program.tiers[0];
  return (
    <div className="cols">
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("programme.threshold")}</h2>
          <span className="chip">{t(program.type === "points" ? "programme.type.points" : "programme.type.stamp")}</span>
        </div>
        {first && <p className="lead">{t("programme.rule", { reward: first.reward, n: first.threshold })}</p>}
        <p className="note">
          {program.min_gap_minutes
            ? t("programme.gap", { n: program.min_gap_minutes })
            : t("programme.gap.none")}
        </p>
        <p className="note">
          {/* Always a number: the tier's own validity, or the shop's reward window
              (019). "Does not lapse" was said for every reward that lapsed at sixty days. */}
          {first ? t("programme.validity", { n: first.lasts_days }) : null}
        </p>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("programme.preview")}</h2>
        </div>
        {/* THE EMPTY CARD A NEW CUSTOMER GETS, not a progress bar (2026-09-18). This
            was a bar fixed at 40% under "What the customer sees", and no customer
            sees a bar at 40%: the app shows one mark per stamp. Found by the UI
            review. Points programmes are said in words; their thresholds are not
            a row of marks. */}
        {first && program.type !== "points" && first.threshold <= 20 && (
          <div className="stamps-row" aria-hidden="true">
            {Array.from({ length: first.threshold }, (_, i) => (
              <span key={i} className="stamp-mark">
                {i + 1}
              </span>
            ))}
          </div>
        )}
        {first && <p className="note">{t("programme.rule", { reward: first.reward, n: first.threshold })}</p>}
        <p className="fine">{t("programme.change")}</p>
      </section>
    </div>
  );
}

export function RewardsScreen({ rewards }: { rewards: Reward[] }) {
  if (rewards.length === 0) return <Empty title="rewards.none.title" body="rewards.none.body" />;
  const issued = rewards.filter((r) => r.type === "REWARD_ISSUE").length;
  const taken = rewards.filter((r) => r.type === "REWARD_USE").length;
  return (
    <>
      <div className="stats">
        <div className="stat">
          <p className="stat-v">{issued}</p>
          <p className="stat-k">{t("rewards.issued")}</p>
        </div>
        <div className="stat">
          <p className="stat-v">{taken}</p>
          <p className="stat-k">{t("rewards.taken")}</p>
        </div>
        <div className="stat">
          <p className="stat-v">{Math.max(0, issued - taken)}</p>
          <p className="stat-k">{t("rewards.owed")}</p>
        </div>
      </div>
      <div className="panel">
        <div className="table-wrap">
          <table className="table-stack" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th role="columnheader">{t("rewards.col.when")}</th>
                <th role="columnheader">{t("rewards.col.who")}</th>
                <th role="columnheader">{t("rewards.col.reward")}</th>
                <th role="columnheader">{t("rewards.col.state")}</th>
                <th role="columnheader">{t("rewards.col.branch")}</th>
                <th role="columnheader">{t("rewards.col.staff")}</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {rewards.map((r) => (
                <tr role="row" key={r.entry_id}>
                  <td role="cell" data-label={t("rewards.col.when")}>{dt(r.at, true)}</td>
                  <td role="cell" data-lead="">{r.who ?? t("customers.erased")}</td>
                  <td role="cell" data-label={t("rewards.col.reward")}>{r.reward ?? "—"}</td>
                  <td role="cell" data-label={t("rewards.col.state")}>
                    <span className="chip" data-tone={r.type === "REWARD_USE" ? "good" : "owed"}>
                      {t(r.type === "REWARD_USE" ? "rewards.taken" : "rewards.issued")}
                    </span>
                  </td>
                  <td role="cell" data-label={t("rewards.col.branch")}>{r.branch ?? "—"}</td>
                  <td role="cell" data-label={t("rewards.col.staff")}>{r.staff ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export function BranchesScreen({ branches }: { branches: Branch[] }) {
  if (branches.length === 0) return <Empty title="branches.none.title" body="branches.none.body" />;
  return (
    <div className="cols">
      {branches.map((b) => (
        <section className="panel" key={b.id}>
          <div className="panel-head">
            <h2 className="panel-title">{b.name}</h2>
            {b.primary && <span className="chip">{t("branches.primary")}</span>}
          </div>
          {b.address && <p className="note">{b.address}</p>}
          <div className="stats">
            <div className="stat">
              <p className="stat-v">{b.stamps.toLocaleString("en-GB")}</p>
              <p className="stat-k">{t("branches.stamps")}</p>
            </div>
            <div className="stat">
              <p className="stat-v">{b.members.toLocaleString("en-GB")}</p>
              <p className="stat-k">{t("branches.members")}</p>
            </div>
            <div className="stat">
              <p className="stat-v">{b.staff}</p>
              <p className="stat-k">{t("branches.staff")}</p>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

function AddPerson({ onAdd }: { onAdd: TeamActions["add"] }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; message: string } | null>(null);
  return (
    <section className="panel form-panel">
      <h2 className="panel-title">{t("team.add.title")}</h2>
      <form
        className="form"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy) return;
          setBusy(true);
          const out = await onAdd(username.trim().toLowerCase(), email.trim());
          setSaid(out);
          if (out.ok) {
            setUsername("");
            setEmail("");
          }
          setBusy(false);
        }}
      >
        <label className="field">
          <span className="field-label">{t("team.add.username")}</span>
          <input
            className="input"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby="team-add-hint"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <p className="fine" id="team-add-hint">
          {t("team.add.usernameHint")}
        </p>
        <label className="field">
          <span className="field-label">{t("team.add.email")}</span>
          <input
            className="input"
            type="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {said && (
          <p className={said.ok ? "form-said" : "form-problem"} role={said.ok ? "status" : "alert"}>
            {said.message}
          </p>
        )}
        <button type="submit" className="btn btn-primary" aria-busy={busy}>
          {t("team.add.submit")}
        </button>
      </form>
    </section>
  );
}

function PersonActions({ person, actions }: { person: Staff; actions: TeamActions }) {
  const [said, setSaid] = useState<string | null>(null);
  const who = person.username ?? person.display_name ?? "";
  return (
    <div className="row-actions">
      <button
        type="button"
        className="btn btn-quiet"
        onClick={async () => setSaid((await actions.resend(person.staff_id)).message)}
      >
        {t("team.resend")}
      </button>
      <button
        type="button"
        className="btn btn-quiet"
        onClick={async () => {
          if (!window.confirm(t("team.removeConfirm", { who }))) return;
          setSaid((await actions.remove(person.staff_id)).message);
        }}
      >
        {t("team.remove")}
      </button>
      {said && (
        <span className="row-said" role="status">
          {said}
        </span>
      )}
    </div>
  );
}

/* A username is words joined by dots, so a long one breaks at a dot
   ("fixture.cashier. / with.a.long.name"), never mid-word as it did at 820. */
function atDots(name: string) {
  return name.split(".").map((part, i, all) => (
    <Fragment key={i}>
      {part}
      {i < all.length - 1 ? (
        <>
          .<wbr />
        </>
      ) : null}
    </Fragment>
  ));
}

export function TeamScreen({ staff, actions }: { staff: Staff[]; actions: TeamActions }) {
  return (
    <div className="stack">
      <AddPerson onAdd={actions.add} />
      {staff.length === 0 ? (
        <Empty title="team.none.title" body="team.none.body" />
      ) : (
        /* PEOPLE, NOT A TABLE (review of 2026-09-18, findings 13 and 14). As a
           table, what the owner came here for -- who has set a password, "Send
           a new link", "Remove" -- sat off the edge of a phone in a sideways
           scroller that did not say it scrolled, and at 820 "Send a new link"
           wrapped onto three lines. A shop's team is a handful of people: each
           is a row that reads across on a wide screen and stacks on a phone,
           with every state labelled and every action 44px. */
        <div className="panel">
          <ul className="people" aria-label={t("team.title")}>
            {staff.map((s) => (
              <li key={s.staff_id} className="person">
                <div className="person-who">
                  <p className="cell-name">{atDots(s.username ?? s.display_name ?? t("customers.erased"))}</p>
                  <p className="person-role">
                    {s.role === "owner"
                      ? t("shell.role.owner")
                      : s.role === "manager"
                        ? t("shell.role.manager")
                        : t("shell.role.staff")}
                  </p>
                </div>
                <div className="person-state">
                  <span className="person-pair">
                    <span className="person-k">{t("team.col.signIn")}</span>
                    <span className="chip" data-tone={s.password_set ? "good" : "warn"}>
                      {t(s.password_set ? "team.status.ready" : "team.status.waiting")}
                    </span>
                  </span>
                  <span className="person-pair">
                    <span className="person-k">{t("team.col.pin")}</span>
                    <span className="chip" data-tone={s.pin_locked ? "warn" : s.has_pin ? "good" : undefined}>
                      {t(s.pin_locked ? "team.pin.locked" : s.has_pin ? "team.pin.set" : "team.pin.none")}
                    </span>
                  </span>
                </div>
                {s.role === "owner" ? <span /> : <PersonActions person={s} actions={actions} />}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="fine">{t("team.invite")}</p>
    </div>
  );
}

export function ExportsScreen({ onCustomers, onRewards }: { onCustomers: () => void; onRewards: () => void }) {
  return (
    <section className="panel">
      <p className="lead">{t("reports.body")}</p>
      <div className="head">
        <button type="button" className="btn btn-secondary" onClick={onCustomers}>
          {t("reports.customers")} · {t("reports.download")}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onRewards}>
          {t("reports.rewards")} · {t("reports.download")}
        </button>
      </div>
      <p className="fine">{t("reports.note")}</p>
    </section>
  );
}

export function SettingsScreen({
  name,
  role,
  contactPhone,
  city,
}: {
  name: string;
  role: string;
  contactPhone: string | null;
  city: string | null;
}) {
  return (
    <div className="cols">
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("settings.shop")}</h2>
        </div>
        <p className="label">{t("settings.name")}</p>
        <p className="lead">{name}</p>
        {city && (
          <>
            <p className="label">{t("settings.city")}</p>
            <p className="lead">{city}</p>
          </>
        )}
        <p className="label">{t("settings.number")}</p>
        {contactPhone ? (
          <p className="lead mono">{contactPhone}</p>
        ) : (
          <p className="note">{t("settings.number.none")}</p>
        )}
        <p className="label">{t("settings.role")}</p>
        <p className="lead">{role}</p>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("settings.plan")}</h2>
        </div>
        <p className="lead">{t("settings.plan.pilot")}</p>
        <p className="note">{t("settings.plan.after")}</p>
        <p className="fine">{t("settings.plan.nothing")}</p>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("settings.data")}</h2>
        </div>
        <p className="note">{t("settings.data.body")}</p>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("settings.close")}</h2>
        </div>
        <p className="note">{t("settings.close.body")}</p>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("settings.contact")}</h2>
        </div>
        <p className="note">{t("settings.contact.body")}</p>
      </section>
    </div>
  );
}

/* --- bringing paper cards across -------------------------------------------
 *
 * THE ONE PLACE THIS DASHBOARD WRITES. Everything else here is a GET, for the
 * reason in App.tsx: the artboard's other writes change cards customers are
 * already holding. An import is a different act. A shop is declaring the
 * customers it already has, the two conflict rules both favour the customer,
 * and anything large or generous enough to be worth questioning is held for a
 * person before it lands.
 *
 * TWO STEPS, AND THE FIRST ONE WRITES NOTHING TO THE LEDGER. Creating the batch
 * produces a plan; the plan is shown; only then is there a button that applies
 * it. That is the shape the API has had since the beginning and it is the whole
 * reason this is safe to hand to a merchant.
 */
export type ImportRow = { phone: string; stamps: number; name: string };
export type ImportPlan = {
  needs_review: boolean;
  review_reason: string | null;
  unique_members: number;
  duplicates: number;
  capped: { phone: string; declared: number; capped_to: number }[];
  ladder_total: number | null;
};

const LOOKS_LIKE_MOBILE = /^\+[1-9]\d{6,14}$/;

export function ImportScreen({
  rows,
  plan,
  busy,
  done,
  problem,
  onAdd,
  onRemove,
  onCheck,
  onApply,
  onStartOver,
}: {
  rows: ImportRow[];
  plan: ImportPlan | null;
  busy: boolean;
  done: number | null;
  problem: string | null;
  onAdd: (row: ImportRow) => string | null;
  onRemove: (phone: string) => void;
  onCheck: () => void;
  onApply: () => void;
  onStartOver: () => void;
}) {
  const [phone, setPhone] = useState("");
  const [stamps, setStamps] = useState("");
  const [name, setName] = useState("");
  const [say, setSay] = useState<string | null>(null);

  if (done !== null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("import.done.title")}</h2>
        </div>
        <p className="lead">{t("import.done.body", { n: done })}</p>
        <div className="head">
          <button type="button" className="btn btn-secondary" onClick={onStartOver}>
            {t("import.addAnother")}
          </button>
        </div>
      </section>
    );
  }

  function add() {
    const trimmed = phone.trim().replace(/[\s-]/g, "");
    if (!LOOKS_LIKE_MOBILE.test(trimmed)) {
      setSay(t("import.badPhone"));
      return;
    }
    const n = Number(stamps);
    if (!Number.isInteger(n) || n < 0 || n > 1000) {
      setSay(t("import.badStamps"));
      return;
    }
    const complaint = onAdd({ phone: trimmed, stamps: n, name: name.trim() });
    if (complaint) {
      setSay(complaint);
      return;
    }
    setSay(null);
    setPhone("");
    setStamps("");
    setName("");
  }

  return (
    <div className="cols">
      <section className="panel">
        <p className="lead">{t("import.body")}</p>
        <form
          className="fields"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <div className="field">
            <label className="field-label" htmlFor="imp-phone">
              {t("import.phone")}
            </label>
            <input
              id="imp-phone"
              className="input"
              inputMode="tel"
              autoComplete="off"
              placeholder={t("import.phonePh")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="imp-stamps">
              {t("import.stamps")}
            </label>
            <input
              id="imp-stamps"
              className="input"
              inputMode="numeric"
              value={stamps}
              onChange={(e) => setStamps(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="imp-name">
              {t("import.name")} · {t("import.nameOptional")}
            </label>
            <input
              id="imp-name"
              className="input"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {say && (
            <p className="error" role="alert">
              {say}
            </p>
          )}
          <button type="submit" className="btn btn-primary">
            {t("import.add")}
          </button>
        </form>
        <p className="fine">{t("import.spreadsheet")}</p>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t("import.rows", { n: rows.length })}</h2>
        </div>
        {rows.length === 0 ? (
          <Empty title="import.empty.title" body="import.empty.body" />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table-stack" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{t("import.phone")}</th>
                    <th role="columnheader">{t("import.name")}</th>
                    <th role="columnheader" className="num">{t("import.stamps")}</th>
                    <th role="columnheader">
                      <span className="sr-only">{t("import.remove")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {rows.map((r) => (
                    <tr role="row" key={r.phone}>
                      <td role="cell" className="mono" data-lead="">{r.phone}</td>
                      <td role="cell" data-label={t("import.name")}>{r.name}</td>
                      <td role="cell" className="num" data-label={t("import.stamps")}>{r.stamps}</td>
                      <td role="cell" className="num">
                        <button type="button" className="btn btn-quiet" onClick={() => onRemove(r.phone)}>
                          {t("import.remove")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {problem && (
              <p className="error" role="alert">
                {problem}
              </p>
            )}
            {!plan && (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={onCheck}>
                {busy ? t("import.checking") : t("import.check")}
              </button>
            )}
          </>
        )}

        {plan && (
          <>
            <div className="panel-head">
              <h2 className="panel-title">{t("import.plan.title")}</h2>
            </div>
            <div className="stats">
              <div className="stat">
                <p className="stat-v">{plan.unique_members}</p>
                <p className="stat-k">{t("import.plan.members")}</p>
              </div>
              <div className="stat">
                <p className="stat-v">{plan.duplicates}</p>
                <p className="stat-k">{t("import.plan.merged")}</p>
              </div>
              <div className="stat">
                <p className="stat-v">{plan.capped.length}</p>
                <p className="stat-k">{t("import.plan.capped")}</p>
              </div>
              {plan.ladder_total !== null && (
                <div className="stat">
                  <p className="stat-v">{plan.ladder_total}</p>
                  <p className="stat-k">{t("import.plan.ladder")}</p>
                </div>
              )}
            </div>
            {plan.duplicates > 0 && <p className="fine">{t("import.plan.mergedBody")}</p>}
            {plan.capped.length > 0 && (
              <>
                <ul className="rows">
                  {plan.capped.map((c) => (
                    <li className="row" key={c.phone}>
                      <span className="dot" data-kind="reward" aria-hidden="true">
                        !
                      </span>
                      <span className="row-title">
                        {t("import.plan.cappedRow", {
                          phone: c.phone,
                          declared: c.declared,
                          capped: c.capped_to,
                        })}
                      </span>
                      <span className="row-value" />
                    </li>
                  ))}
                </ul>
                <p className="fine">{t("import.plan.cappedBody")}</p>
              </>
            )}

            {plan.needs_review ? (
              <div className="empty">
                <p className="empty-title">{t("import.review.title")}</p>
                <p className="empty-body">{t("import.review.body")}</p>
              </div>
            ) : (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={onApply}>
                {busy ? t("import.applying") : t("import.apply")}
              </button>
            )}
            <button type="button" className="btn btn-quiet" onClick={onStartOver}>
              {t("import.startOver")}
            </button>
          </>
        )}
      </section>
    </div>
  );
}

/* --- the card that goes on the counter -------------------------------------
 *
 * PRINTED FROM THE BROWSER, not generated on a server. The merchant presses
 * Print and their own browser makes the PDF, which needs no dependency, no
 * font embedding and no rendering service, and it is the only version of this
 * that works the same on a laptop in a shop as it does here.
 *
 * TWO PAGES ON A5, because @page carries one size per document and two named
 * page sizes is patchy across browsers. The table card fills the first sheet;
 * the window sticker sits on the second with a line to cut along. Both come out
 * of one press, which is what a shop wants.
 */
export function CardScreen({ name, program }: { name: string; program: Program }) {
  const tier = program?.tiers?.[0] ?? null;
  if (!tier) return <Empty title="card.noProgramme.title" body="card.noProgramme.body" />;

  const reward = t("card.p.reward", { reward: tier.reward, n: tier.threshold });

  return (
    <>
      <section className="panel no-print">
        <p className="lead">{t("card.body")}</p>
        <div className="head">
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            {t("card.print")}
          </button>
        </div>
        <p className="label">{t("card.what")}</p>
        <p className="note">{t("card.what.body")}</p>
        <p className="fine">{t("card.noApp")}</p>
        <p className="fine">{t("card.english")}</p>
      </section>

      <div className="sheets">
        <div className="sheet" data-kind="table">
          <div className="sheet-in">
            <p className="sheet-mark">{t("card.p.mark")}</p>
            <h2 className="sheet-shop">{name}</h2>
            <p className="sheet-collect">{t("card.p.collect")}</p>
            <p className="sheet-reward">{reward}</p>
            <p className="sheet-join">{t("card.p.join")}</p>
            <p className="sheet-how">{t("card.p.how")}</p>
            <p className="sheet-check">{t("card.p.check")}</p>
          </div>
        </div>

        <div className="sheet" data-kind="sticker">
          <div className="sticker">
            <p className="sheet-mark">{t("card.p.mark")}</p>
            <h2 className="sticker-shop">{name}</h2>
            <p className="sticker-collect">{t("card.p.collect")}</p>
            <p className="sticker-reward">{reward}</p>
          </div>
          <p className="cut">{t("card.p.cut")}</p>
        </div>
      </div>
    </>
  );
}
